import React, {
  FC, useCallback, useEffect, useMemo,
} from 'react';
import styled from 'styled-components';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import { defer, interval, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import AgoraRTC, { IAgoraRTCClient, ILocalAudioTrack } from 'agora-rtc-sdk-ng';
import { useObservable } from 'react-use';
import { Videocam, VideocamOff } from '@material-ui/icons';
import LiveStreamBoothExhibitorPanelVolumeIndicator
  from 'components/pictureInPicture/LiveStreamBoothExhibitorPanel/LiveStreamBoothExhibitorPanelVolumeIndicator';
import { useSelector } from 'react-redux';
import LiveStreamBoothExhibitorPanelBottomBarDeviceSelect
  from 'components/pictureInPicture/LiveStreamBoothExhibitorPanel/LiveStreamBoothExhibitorPanelBottomBarDeviceSelect';

const StyledControlRow = styled.div`
  padding: 4px 4px;
  display: flex;
  justify-content: space-around;
  flex: 0 0 auto;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;

  & > * {
    margin: 2px 4px;
  }
`;

const StyledControlCol = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledNotDuringPeriodsMessage = styled.div`
  color: #cf2f2f;
  display: flex;
  align-items: center;
  padding: 2px 10px;
`;

const StyledStopStreamingVideoButton = styled(Button)`
  flex: 0 0 auto;
  white-space: nowrap;
`;

const EMPTY_PERIODS = [];

export interface LiveStreamBoothExhibitorPanelPreviewBottomBarProps {
  selectedCameraId: string;
  setSelectedCameraId: (id: string) => void;
  isCameraOn: boolean;
  setIsCameraOn: (isOn: boolean) => void;
  selectedMicrophoneId: string;
  setSelectedMicrophoneId: (id: string) => void;
  isMicrophoneOn: boolean;
  setIsMicrophoneOn: (isOn: boolean) => void;
  client: IAgoraRTCClient;
  audioTrack: ILocalAudioTrack;
  isStreaming: boolean;
  setIsStreaming: (streaming: boolean) => void;
  boothId: string;
  duringPeriods: boolean;
}

const EMPTY_ARRAY = [];

const LiveStreamBoothExhibitorPanelBottomBar: FC<LiveStreamBoothExhibitorPanelPreviewBottomBarProps> = ({
  selectedCameraId,
  setSelectedCameraId,
  isCameraOn,
  setIsCameraOn,
  selectedMicrophoneId,
  setSelectedMicrophoneId,
  isMicrophoneOn,
  setIsMicrophoneOn,
  audioTrack,
  client,
  isStreaming,
  setIsStreaming,
  boothId,
  duringPeriods,
}) => {
  const cameras$ = useMemo(() => defer(() => AgoraRTC.getCameras()), []);
  const cameras = useObservable(cameras$) ?? EMPTY_ARRAY;
  useEffect(() => {
    if ((selectedCameraId == null || cameras.find((it) => it.deviceId === selectedCameraId) == null) && cameras?.length > 0) {
      setSelectedCameraId(cameras[0]?.deviceId);
    }
  }, [cameras, selectedCameraId, setSelectedCameraId]);

  const microphones$ = useMemo(() => defer(() => AgoraRTC.getMicrophones()), []);
  const microphones = useObservable(microphones$) ?? EMPTY_ARRAY;

  useEffect(() => {
    if ((selectedMicrophoneId == null || microphones.find((it) => it.deviceId === selectedMicrophoneId) == null) && microphones?.length > 0) {
      setSelectedMicrophoneId(microphones[0]?.deviceId);
    }
  }, [microphones, selectedMicrophoneId, setSelectedMicrophoneId]);

  const toggleCamera = useCallback(() => setIsCameraOn(!isCameraOn), [isCameraOn, setIsCameraOn]);
  const toggleMicrophone = useCallback(() => setIsMicrophoneOn(!isMicrophoneOn), [isMicrophoneOn, setIsMicrophoneOn]);

  const selectedVerifiedCameraId = useMemo(() => ((cameras).find((it) => it.deviceId === selectedCameraId)?.deviceId) ?? cameras[0]?.deviceId,
    [cameras, selectedCameraId]);

  const selectedVerifiedMicrophoneId = useMemo(() => ((microphones).find((it) => it.deviceId === selectedMicrophoneId)?.deviceId) ?? microphones[0]?.deviceId,
    [microphones, selectedMicrophoneId]);

  const haveVideoTrackStreaming$ = useMemo(() => new Observable<boolean>((subscriber) => {
    client.on('user-published', (user, mediaType) => {
      if (user.uid !== client.uid && mediaType === 'video') subscriber.next(true);
    });
    client.on('user-unpublished', (user, mediaType) => {
      if (user.uid !== client.uid && mediaType === 'video') subscriber.next(false);
    });
  }), [client]);

  const haveVideoTrackStreaming = useObservable(haveVideoTrackStreaming$) ?? client.remoteUsers.find((it) => it.hasVideo) != null;

  const startLiveStreamButtonText = useMemo(() => {
    if (!duringPeriods) return 'Not during periods';
    if (haveVideoTrackStreaming) return 'Another user is streaming the video track';
    if (selectedCameraId == null && selectedMicrophoneId == null) return 'No selected microphone or camera';
    return 'Start Streaming';
  }, [duringPeriods, haveVideoTrackStreaming, selectedCameraId, selectedMicrophoneId]);

  return (
    <>
      <StyledControlRow>
        <StyledControlCol>
          <Tooltip title={isCameraOn ? 'Video streaming' : 'Video streaming stopped'}>
            <IconButton onClick={toggleCamera} disabled={selectedVerifiedCameraId == null || haveVideoTrackStreaming}>
              {isCameraOn ? <Videocam /> : <VideocamOff />}
            </IconButton>
          </Tooltip>
          {
            cameras.length === 0 ? 'No camera detected' : (
              <LiveStreamBoothExhibitorPanelBottomBarDeviceSelect
                isStreaming={isStreaming}
                devices={cameras}
                selectedDeviceId={selectedCameraId}
                setSelectedDeviceId={setSelectedCameraId}
                deviceTypeLabel="Camera"
              />
            )
          }
        </StyledControlCol>
        <StyledControlCol>
          <LiveStreamBoothExhibitorPanelVolumeIndicator
            toggleAudio={toggleMicrophone}
            selectedVerifiedMicrophoneId={selectedVerifiedMicrophoneId}
            audioTrack={audioTrack}
          />
          {microphones.length === 0 ? 'No microphone detected' : (
            <LiveStreamBoothExhibitorPanelBottomBarDeviceSelect
              isStreaming={isStreaming}
              devices={microphones}
              selectedDeviceId={selectedMicrophoneId}
              setSelectedDeviceId={setSelectedMicrophoneId}
              deviceTypeLabel="Microphone"
            />
          )}
        </StyledControlCol>
        {!isStreaming && !duringPeriods && (
          <Tooltip
            title="You can still test your camera and microphone here, but none of the attendees will be able to join the live stream until periods start."
          >
            <StyledNotDuringPeriodsMessage>Not during live stream periods!</StyledNotDuringPeriodsMessage>
          </Tooltip>
        )}
        {isStreaming && (
          <StyledStopStreamingVideoButton
            color="primary"
            variant="contained"
            onClick={() => setIsStreaming(false)}
          >
            Stop Streaming
          </StyledStopStreamingVideoButton>
        )}
      </StyledControlRow>
      {!isStreaming && (
        <StyledControlRow>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            onClick={() => setIsStreaming(true)}
            disabled={haveVideoTrackStreaming || (selectedMicrophoneId == null && selectedCameraId == null) || !duringPeriods}
          >
            {startLiveStreamButtonText}
          </Button>
        </StyledControlRow>
      )}
    </>
  );
};

export default LiveStreamBoothExhibitorPanelBottomBar;
